import React, { useCallback, useState, useImperativeHandle } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import Selector, {
  MAX_WIDTH_FOR_SELECTOR,
  SelectorOptions,
} from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import { BottomBar } from '../BottomBar';
import { OtherTextField } from '../ImpactSummary/SectionService';
import {
  LessonValue,
  NARRATIVE_LESSON_QUESTION_ID,
  NarrativeLessonProps,
  NarrativeSubmitReference,
} from './utils';
import IndicatorTextareaRender from 'npo/pages/ReportBuilder/Components/ImpactIndicator/IndicatorTextareaRender.tsx';
import { StyledTrueImpactText } from '../ImpactIndicator/IndicatorTextarea';


const isValid = (lesson: string[], description: string): boolean =>
  lesson.length > 0 && description.length > 0;

const getDefaultFilters = (selectedOptions: string[]): string[] => {
  const result: string[] = [];
  selectedOptions.forEach(x => {
    if (options.includes(x)) result.push(x);
  });
  return result;
};


const options = [
  'Planning',
  'Training',
  'Implementation',
  'Measurement & Reporting',
  'Stakeholder Management',
  'Donor Relations',
];

interface ExtendedNarrativeLessonProps extends NarrativeLessonProps {
  handlePrevious: () => unknown;
  handleNext: (lesson: LessonValue) => unknown;
}

const StyledDiv = styled.div`
  padding-bottom: 150px;
  .question-text {
    max-width: 730px;
    margin-top: ${SPACERS.large};
    margin-left: auto;
    margin-right: auto;
  }
  .sub-heading {
    max-width: 502px;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.large};
  }
  .lesson-content {
    max-width: 622px;
    margin-left: auto;
    margin-right: auto;
    .lesson-sub-text {
      margin-top: ${SPACERS.tiny};
      margin-bottom: ${SPACERS.medium};
    }
    .lesson-selections {
      display: grid;
      grid-auto-flow: column;
      grid-template-rows: repeat(4, 1fr);
      gap: 0px ${SPACERS.small};
      .other-field {
        margin-bottom: 0px !important;
        .MuiOutlinedInput-root {
          height: 100%;
        }
      }
      & > div {
        height: 64px;
      }
    }
  }
  .lesson-description {
    height: 150px;
    margin-top: ${SPACERS.medium};
  }
`;

const getOtherValue = (
  lesson: NarrativeLessonProps['storyLesson'],
): string | undefined => {
  for (const l of lesson) {
    if (!options.includes(l)) return l;
  }
  return undefined;
};

const NarrativeLesson = React.forwardRef<
  NarrativeSubmitReference,
  ExtendedNarrativeLessonProps & {
    isThirdParty: boolean;
  }
>(
  (
    {
      storyLesson,
      hideBottomBar,
      lessonDescription,
      handleNext,
      handlePrevious,
    },
    forwardRef,
  ) => {
    const [error, setError] = useState<string | undefined>(undefined);
    const [otherText, setOtherText] = useState<string | undefined>(
      getOtherValue(storyLesson),
    );
    const [description, setDescription] = useState<string>(lessonDescription);
    const [lesson, setLesson] = useState<string[]>(storyLesson);

    const handleOtherOptionClick = useCallback(() => {
      setLesson(s => getDefaultFilters(s));
      setOtherText(o => (o === undefined ? '' : undefined));
    }, [setOtherText]);

    const handleWrite = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setOtherText(e.target.value);
      },
      [setOtherText],
    );

    const handleLesson = useCallback(
      (value: string) => {
        setLesson(s => {
          const newState = [...s];
          const index = newState.findIndex(x => x === value);
          if (index === -1) {
            newState.push(value);
          } else {
            newState.splice(index, 1);
          }
          return newState;
        });
      },
      [setLesson],
    );

    const handleBlur = useCallback(() => {
      setLesson(s => {
        const result = getDefaultFilters(s);
        if (!otherText) return result;
        result.push(otherText);
        return result;
      });
    }, [otherText, setLesson]);

    const onSubmit = () => {
      handleNext({
        lesson,
        text: description,
      });
    };

    useImperativeHandle(forwardRef, () => ({
      isValid: () => isValid(lesson, description),
      handleSubmit: () => ({ lesson, text: description }),
    }));

    return (
      <>
        <StyledDiv>
          <TrueImpactText
            variant={hideBottomBar ? 'heading-subheading' : 'heading-page'}
            className="question-text"
            align="center"
          >
            What lessons learned would you like to share?
          </TrueImpactText>
          <TrueImpactText className="sub-heading" align="center">
            These could be anything from hiring challenges, to industry-specific
            hurdles, budget challenges, etc.
          </TrueImpactText>
          <div className="lesson-content">
            <TrueImpactText variant="body-bold" align="center">
              What kind of lesson or advice would you say this is?
            </TrueImpactText>
            <TrueImpactText
              variant="body"
              className="lesson-sub-text"
              align="center"
            >
              (Check all that apply)
            </TrueImpactText>
            <div className="lesson-selections">
              <SelectorOptions
                variant="checkbox"
                options={options}
                getLabel={v => v}
                getSelected={val => lesson.includes(val)}
                onClick={handleLesson}
              />
              <Selector
                variant="checkbox"
                label="Other"
                selected={otherText !== undefined}
                maxWidth={MAX_WIDTH_FOR_SELECTOR}
                margin={`${SPACERS.tiny} auto`}
                onClick={handleOtherOptionClick}
              />
              {otherText !== undefined && (
                <OtherTextField
                  value={otherText}
                  placeholder="Please specify"
                  onChange={handleWrite}
                  onBlur={handleBlur}
                  autoFocus={otherText === ''}
                  className="other-field"
                />
              )}
            </div>
            <StyledTrueImpactText
              variant="body"
              textareaBackgroundColor={'whiteBase'}
            >
              <IndicatorTextareaRender
                id="lesson-description"
                questionId={NARRATIVE_LESSON_QUESTION_ID}
                handleSave={(e) => {
                  setDescription(e);
                  setError(e.length < 1 ? 'Description is required' : '');
                }}
                initialValue={description}
                placeholder="Enter your lessons learned here...."
                textareaClassName={error ? 'textarea-error' : undefined}
                maxLength={900}
              />
            </StyledTrueImpactText>
          </div>
        </StyledDiv>
        {!hideBottomBar && (
          <BottomBar
            disabled={false}
            hideBack={false}
            hideNext={!isValid(lesson, description)}
            handleBack={handlePrevious}
            handleNext={onSubmit}
            backTitle="Back"
            nextTitle="Save & Continue"
          />
        )}
      </>
    );
  },
);

export default NarrativeLesson;
